import { useLayoutEffect, useState } from 'react';
import {
  FlexColumn,
  TextField,
  formFieldRequiredLabel,
} from '@elementary/common-ui/components';
import {
  ErrorMessage,
  InputWrapper,
} from '@elementary/common-ui/components/FormControls/FormControls';
import { useForm } from 'react-hook-form';
import * as api from 'shared/services/settingsApi';

export type ServiceNowAppConfig = {
  configKey: string;
  subdomain: string;
};

export type ServiceNowDialogConfig = {
  isValid: boolean;
  onSubmit: () => void;
  reset: () => void;
  error?: { message: string };
  loading?: boolean;
};

type ServiceNowAppConfigForm = {
  clientId: string;
  clientSecret: string;
  subdomain: string;
};

type Props = {
  envId: string;
  onCreate: (config: ServiceNowAppConfig) => void;
  onDialogProps: (config: ServiceNowDialogConfig) => void;
};

const ServiceNowAppConfigContent = ({
  envId,
  onCreate,
  onDialogProps,
}: Props) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ServiceNowAppConfigForm>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFormSubmit = async (data: ServiceNowAppConfigForm) => {
    setIsLoading(true);
    setError(null);
    try {
      const { config_key } = await api.createServiceNowConfig(envId, {
        client_id: data.clientId,
        client_secret: data.clientSecret,
      });
      onCreate({ configKey: config_key, subdomain: data.subdomain });
    } catch {
      setError(
        'Failed to create ServiceNow app config, contact us for support',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const clientId = watch('clientId');
  const clientSecret = watch('clientSecret');
  const subdomain = watch('subdomain');

  useLayoutEffect(() => {
    onDialogProps({
      isValid: !!clientId && !!clientSecret && !!subdomain,
      onSubmit: handleSubmit(handleFormSubmit),
      reset,
      error: error ? { message: error } : undefined,
      loading: isLoading,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clientSecret, subdomain, error, isLoading]);

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <InputWrapper>
        <TextField
          label="Client ID"
          {...register('clientId', { required: formFieldRequiredLabel })}
          disabled={isLoading}
          size="large"
        />
      </InputWrapper>
      <InputWrapper>
        <TextField
          label="Client Secret"
          {...register('clientSecret', { required: formFieldRequiredLabel })}
          disabled={isLoading}
          type="password"
          size="large"
        />
      </InputWrapper>
      <InputWrapper>
        <TextField
          label="Subdomain"
          {...register('subdomain', {
            required: formFieldRequiredLabel,
            pattern: {
              value: /^[a-z0-9-]+$/,
              message:
                'Include only the subdomain of your ServiceNow instance (e.g. for https://my-company.service-now.com, the subdomain is my-company)',
            },
          })}
          disabled={isLoading}
          size="large"
        />
        {errors.subdomain && (
          <ErrorMessage>{errors.subdomain.message}</ErrorMessage>
        )}
      </InputWrapper>
    </FlexColumn>
  );
};

export default ServiceNowAppConfigContent;
